import React, { useEffect, useState } from 'react';
import { pageTitle } from '@/lib/appConfig';
import {
  fetchBranches,
  createBranch,
  updateBranch,
  deleteBranch,
  setMainBranch,
} from '@/api';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Building2, Plus, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BranchDialog } from '@/components/branches/BranchDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Branch } from '@/types/branch';
import { toast } from 'sonner';

export default function BranchesPage() {
  useEffect(() => {
    document.title = pageTitle('Branches');
  }, []);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [loadingMainId, setLoadingMainId] = useState<string | null>(null);

  // Load branches on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchBranches();
        setBranches(res.data);
      } catch (err: any) {
        toast.error('Failed to load branches');
      }
    })();
  }, []);

  // Add
  const handleAddBranch = async (data: Omit<Branch, 'id' | 'isMain'>) => {
    try {
      const res = await createBranch(data);
      setBranches([...branches, res.data]);
    } catch (err: any) {
      throw new Error(err.message || 'Could not create branch');
    }
  };

  // Edit
  const handleEditBranch = async (data: Omit<Branch, 'id' | 'isMain'>) => {
    if (!editingBranch) return;
    try {
      const res = await updateBranch(editingBranch.id, data);
      setBranches(
        branches.map(b => (b.id === editingBranch.id ? res.data : b))
      );
    } catch (err: any) {
      throw new Error(err.message || 'Could not update branch');
    }
  };

  // Delete
  const handleDelete = async (id: string) => {
    const branchToDelete = branches.find(b => b.id === id);
    if (branchToDelete?.isMain) {
      toast.error('Cannot delete the main branch');
      return;
    }
    try {
      await deleteBranch(id);
      setBranches(branches.filter(b => b.id !== id));
      toast.success('Branch deleted successfully');
    } catch (err: any) {
      toast.error(err.message || 'Delete failed');
    }
  };

  // Set as Main Branch
  const handleSetMain = async (id: string) => {
    setLoadingMainId(id);
    try {
      const res = await setMainBranch(id);
      const updatedBranches = branches.map(b => ({
        ...b,
        isMain: b.id === id ? res.data.isMain : false,
      }));
      setBranches(updatedBranches); // Force state update
      toast.success('Main branch updated successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to set main branch');
      // Fallback: Reload branches if state update fails
      try {
        const res = await fetchBranches();
        setBranches(res.data);
      } catch (reloadErr: any) {
        toast.error('Failed to refresh branches');
      }
    } finally {
      setLoadingMainId(null);
    }
  };

  // Unset Main Branch
  const handleUnsetMain = async (id: string) => {
    try {
      const branch = branches.find(b => b.id === id);
      if (!branch) return;
      const data = { ...branch, isMain: false };
      const res = await updateBranch(id, data);
      const updatedBranches = branches.map(b => (b.id === id ? { ...res.data, isMain: false } : b));
      setBranches(updatedBranches); // Force state update
      toast.success('Main branch unset successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to unset main branch');
      // Fallback: Reload branches if state update fails
      try {
        const res = await fetchBranches();
        setBranches(res.data);
      } catch (reloadErr: any) {
        toast.error('Failed to refresh branches');
      }
    }
  };

  // Sort branches: main branch at the top
  const sortedBranches = [...branches].sort((a, b) => {
    if (a.isMain && !b.isMain) return -1;
    if (!a.isMain && b.isMain) return 1;
    return a.name.localeCompare(b.name);
  });

  // Check if there is a main branch
  const hasMainBranch = branches.some(branch => branch.isMain);

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Branches</h1>
          <p className="text-muted-foreground mt-1">
            Manage your organization's branches.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingBranch(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> Add Branch
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <CardTitle>Branch Management</CardTitle>
          </div>
          <CardDescription>
            Add, edit, and manage your branches
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Branch Name</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Main Branch</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedBranches.map(branch => (
                <TableRow
                  key={branch.id}
                  className={branch.isMain ? 'bg-green-50 border-l-4 border-green-500' : ''}
                >
                  <TableCell className="font-medium">
                    {branch.name}
                  </TableCell>
                  <TableCell>{branch.address}</TableCell>
                  <TableCell>{branch.phone}</TableCell>
                  <TableCell>{branch.isMain ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingBranch(branch);
                          setDialogOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      {branch.isMain ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUnsetMain(branch.id)}
                          className="border-red-500 text-red-500 hover:bg-red-50"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Unset as Main
                        </Button>
                      ) : !hasMainBranch && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSetMain(branch.id)}
                          disabled={loadingMainId === branch.id}
                          className="border-blue-500 text-blue-500 hover:bg-blue-50"
                        >
                          {loadingMainId === branch.id ? (
                            'Setting...'
                          ) : (
                            <>
                              <Check className="h-4 w-4 mr-1" />
                              Set as Main
                            </>
                          )}
                        </Button>
                      )}
                      {!branch.isMain && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(branch.id)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <BranchDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={editingBranch ? handleEditBranch : handleAddBranch}
        defaultValues={editingBranch || undefined}
        mode={editingBranch ? 'edit' : 'add'}
      />
    </div>
  );
}
